import React from 'react';
import { FiX, FiMapPin, FiGlobe, FiCalendar, FiUsers, FiZap, FiMap, FiImage } from 'react-icons/fi';
import formatDate from '../../utils/format-date';


enum PitchType {
    ROUGH = "rough",
    CEMENT = "cement",
    TURF = "turf",
    MATTING = "matting",
    ASTROTURF = "astroturf",
}

interface IGround {
    id: number;
    name: string;
    country: string;
    city: string;
    address?: string;
    pitch_type: string;
    lighting_available: boolean;
    capacity?: number;
    status: string;
    source?: string;
    latitude?: number;
    longitude?: number;
    image_urls?: string[];
    created_at: string;
    updated_at: string;
}

interface ViewGroundOffcanvasProps {
    ground: IGround | null;
    showViewGroundCanvas: boolean;
    setShowViewGroundCanvas: (show: boolean) => void;
}

const ViewGroundOffcanvas: React.FC<ViewGroundOffcanvasProps> = ({
    ground,
    showViewGroundCanvas,
    setShowViewGroundCanvas
}) => {
    const handleClose = () => {
        setShowViewGroundCanvas(false);
    };

    // Get status style
    const getStatusStyle = (status: string) => {
        switch (status.toLowerCase()) {
            case 'active': return 'bg-green-100 text-green-800';
            case 'inactive': return 'bg-red-100 text-red-800';
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'draft': return 'bg-blue-100 text-blue-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    // Get pitch type style
    const getPitchTypeStyle = (pitchType: string) => {
        switch (pitchType) {
            case PitchType.TURF: return 'bg-green-100 text-green-800';
            case PitchType.MATTING: return 'bg-blue-100 text-blue-800';
            case PitchType.CEMENT: return 'bg-gray-100 text-gray-800';
            case PitchType.ASTROTURF: return 'bg-stone-100 text-stone-800';
            case PitchType.ROUGH: return 'bg-amber-100 text-amber-800';
            default: return 'bg-purple-100 text-purple-800';
        }
    };

    // Get source style
    const getSourceStyle = (source: string) => {
        switch (source.toLowerCase()) {
            case 'api': return 'bg-indigo-100 text-indigo-800';
            case 'manual': return 'bg-slate-100 text-slate-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (!ground) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${showViewGroundCanvas ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                onClick={handleClose}
            />

            {/* Offcanvas */}
            <div className={`fixed top-0 right-0 h-full w-full max-w-4xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${showViewGroundCanvas ? 'translate-x-0' : 'translate-x-full'
                }`}>

                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                        <FiMapPin className="mr-2" />
                        Ground Details
                    </h2>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <FiX className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex flex-col h-full">
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">

                        {/* Ground Header */}
                        <div className="bg-gray-50 p-6 rounded-lg">
                            <div className="flex items-start space-x-4">
                                {ground.image_urls && ground.image_urls.length > 0 && (
                                    <img
                                        src={ground.image_urls[0]}
                                        alt={ground.name}
                                        className="w-20 h-16 object-cover rounded-lg border"
                                    />
                                )}
                                <div className="flex-1">
                                    <h4 className="text-2xl font-bold text-gray-900 mb-2">{ground.name}</h4>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className={`px-3 py-1 capitalize text-sm rounded-full font-medium ${getStatusStyle(ground.status)}`}>
                                            {ground.status}
                                        </span>
                                        <span className={`px-3 py-1 capitalize text-sm rounded-full font-medium ${getPitchTypeStyle(ground.pitch_type)}`}>
                                            {ground.pitch_type}
                                        </span>
                                        {ground.source && (
                                            <span className={`px-3 py-1 capitalize text-sm rounded-full font-medium ${getSourceStyle(ground.source)}`}>
                                                {ground.source}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Ground Images */}
                        {ground.image_urls && ground.image_urls.length > 0 && (
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-gray-900 border-b pb-2 flex items-center">
                                    <FiImage className="mr-2" />
                                    Ground Images
                                </h3>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                    {ground.image_urls.map((image, index) => (
                                        <div key={index} className="aspect-w-16 aspect-h-12 rounded-lg overflow-hidden bg-gray-100">
                                            <img
                                                src={image}
                                                alt={`Ground image ${index + 1}`}
                                                className="w-full h-32 object-cover rounded-lg hover:scale-105 transition-transform cursor-pointer"
                                                onClick={() => window.open(image, '_blank')}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Basic Information */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Basic Information</h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-4">
                                    <div className="flex items-start space-x-3">
                                        <FiGlobe className="w-5 h-5 text-gray-500 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-700">Location</p>
                                            <p className="text-gray-900">{ground.city}, {ground.country}</p>
                                        </div>
                                    </div>

                                    {ground.address && (
                                        <div className="flex items-start space-x-3">
                                            <FiMapPin className="w-5 h-5 text-gray-500 mt-0.5" />
                                            <div>
                                                <p className="text-sm font-medium text-gray-700">Address</p>
                                                <p className="text-gray-900">{ground.address}</p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex items-start space-x-3">
                                        <FiZap className="w-5 h-5 text-gray-500 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-700">Lighting</p>
                                            <div className="flex items-center gap-2">
                                                <p className="text-gray-900">
                                                    {ground.lighting_available ? 'Available' : 'Not Available'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    {ground.capacity && (
                                        <div className="flex items-start space-x-3">
                                            <FiUsers className="w-5 h-5 text-gray-500 mt-0.5" />
                                            <div>
                                                <p className="text-sm font-medium text-gray-700">Capacity</p>
                                                <p className="text-gray-900 text-lg font-semibold">{ground.capacity.toLocaleString()}</p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex items-start space-x-3">
                                        <FiCalendar className="w-5 h-5 text-gray-500 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-700">Created Date</p>
                                            <p className="text-gray-900">{formatDate(ground.created_at)}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-start space-x-3">
                                        <FiCalendar className="w-5 h-5 text-gray-500 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-medium text-gray-700">Last Updated</p>
                                            <p className="text-gray-900">{formatDate(ground.updated_at)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Location Map */}
                        {(ground.latitude && ground.longitude) && (
                            <div className="space-y-4">
                                <h3 className="text-lg font-medium text-gray-900 border-b pb-2 flex items-center">
                                    <FiMap className="mr-2" />
                                    Location Map
                                </h3>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    {/* Map Container */}
                                    <div className="w-full h-80 rounded-lg overflow-hidden shadow-md bg-gray-200">
                                        <iframe
                                            width="100%"
                                            height="100%"
                                            frameBorder="0"
                                            style={{ border: 0 }}
                                            src={`https://maps.google.com/maps?q=${ground.latitude},${ground.longitude}&z=15&output=embed`}
                                            allowFullScreen
                                            loading="lazy"
                                            referrerPolicy="no-referrer-when-downgrade"
                                            title={`Map of ${ground.name}`}
                                        />
                                    </div>

                                    {/* Coordinates Info */}
                                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="flex items-center space-x-3">
                                            <FiMap className="w-5 h-5 text-gray-500" />
                                            <div>
                                                <p className="text-sm font-medium text-gray-700">Latitude</p>
                                                <p className="text-gray-900 font-mono text-sm">{ground.latitude}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-3">
                                            <FiMap className="w-5 h-5 text-gray-500" />
                                            <div>
                                                <p className="text-sm font-medium text-gray-700">Longitude</p>
                                                <p className="text-gray-900 font-mono text-sm">{ground.longitude}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Open in Google Maps Button */}
                                    <div className="mt-4 pt-4 border-t border-gray-200">
                                        <a
                                            href={`https://www.google.com/maps?q=${ground.latitude},${ground.longitude}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors text-sm"
                                        >
                                            <FiMap className="w-4 h-4 mr-2" />
                                            Open in Google Maps
                                        </a>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Ground Specifications */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Ground Specifications</h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                                        <div className={`text-2xl font-bold mb-1 capitalize ${getPitchTypeStyle(ground.pitch_type).includes('green') ? 'text-green-600' :
                                            getPitchTypeStyle(ground.pitch_type).includes('blue') ? 'text-blue-600' :
                                                getPitchTypeStyle(ground.pitch_type).includes('gray') ? 'text-gray-600' :
                                                    getPitchTypeStyle(ground.pitch_type).includes('stone') ? 'text-stone-600' :
                                                        getPitchTypeStyle(ground.pitch_type).includes('emerald') ? 'text-emerald-600' :
                                                            getPitchTypeStyle(ground.pitch_type).includes('orange') ? 'text-orange-600' :
                                                                getPitchTypeStyle(ground.pitch_type).includes('amber') ? 'text-amber-600' : 'text-purple-600'}`}>
                                            {ground.pitch_type}
                                        </div>
                                        <div className="text-sm text-gray-500">Pitch Type</div>
                                    </div>

                                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                                        <div className={`text-2xl font-bold mb-1 ${ground.lighting_available ? 'text-green-600' : 'text-red-600'}`}>
                                            {ground.lighting_available ? 'Yes' : 'No'}
                                        </div>
                                        <div className="text-sm text-gray-500">Night Lighting</div>
                                    </div>

                                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                                        <div className="text-2xl font-bold text-primary-600 mb-1">
                                            {ground.capacity ? ground.capacity.toLocaleString() : 'N/A'}
                                        </div>
                                        <div className="text-sm text-gray-500">Capacity</div>
                                    </div>

                                    <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                                        <div className={`text-xl font-bold mb-1 capitalize ${ground.source === 'api' ? 'text-indigo-600' : 'text-slate-600'}`}>
                                            {ground.source || 'Manual'}
                                        </div>
                                        <div className="text-sm text-gray-500">Data Source</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Additional Details */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Additional Details</h3>
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="font-medium text-gray-700">Ground ID:</span>
                                            <span className="text-gray-900">#{ground.id}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="font-medium text-gray-700">Status:</span>
                                            <span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusStyle(ground.status)}`}>
                                                {ground.status}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="font-medium text-gray-700">Source:</span>
                                            <span className={`px-2 py-1 text-xs rounded-full capitalize ${getSourceStyle(ground.source || 'manual')}`}>
                                                {ground.source || 'Manual'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="font-medium text-gray-700">Images:</span>
                                            <span className="text-gray-900">
                                                {ground.image_urls ? ground.image_urls.length : 0} uploaded
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="font-medium text-gray-700">Coordinates:</span>
                                            <span className="text-gray-900">
                                                {ground.latitude && ground.longitude ? 'Available' : 'Not set'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="p-6 border-t border-gray-200 flex justify-center mb-20">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-8 py-2 border border-gray-300 rounded-md shadow-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ViewGroundOffcanvas;